import type { LangCode, MeetingStage, UserRole } from "@scoach/types";
import { CUSTOM_QUESTION_PRESETS, LANGUAGES, userLangToLangCode } from "@scoach/types";
import { Button, Card, Field, SegmentedControl, useToast } from "@scoach/ui";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { MeetingLabels } from "../../../components/MeetingLabels.tsx";
import { authApi } from "../../auth/api.ts";
import { useAuthStore } from "../../auth/store.ts";
import { useAvailableRoles } from "../../../lib/roles.ts";
import { CompanyPicker } from "../../companies/CompanyPicker.tsx";
import { CompanyReportPanel } from "../../companies/CompanyReportPanel.tsx";
import { companiesApi } from "../../companies/api.ts";
import { summaryApi } from "../../summary/api.ts";
import { preMeetingApi } from "../api.ts";
import { usePreMeetingStore } from "../store.ts";
import { AutoFillBanner } from "./AutoFillBanner.tsx";

const TEMPLATES: { label: string; goal: string }[] = [
  {
    label: "First intro",
    goal: "First introduction call. Build rapport, understand their cloud strategy, and identify the most critical pain point we could help with.",
  },
  {
    label: "Commercial alignment",
    goal: "Align on commercial terms: budget range, decision criteria, procurement timeline, and any existing AWS/Azure commitments.",
  },
  {
    label: "Technical deep-dive",
    goal: "Technical deep-dive on the proposed architecture. Validate latency / cost / compliance assumptions and identify integration risks.",
  },
  {
    label: "Procurement",
    goal: "Walk through procurement: contract terms, security review path, MSA red lines, and target signature date.",
  },
];

export interface MeetingSetupPageProps {
  meetingId: string;
}

export function MeetingSetupPage({ meetingId }: MeetingSetupPageProps) {
  const s = usePreMeetingStore();
  const nav = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(!s.hydrated);
  const [saving, setSaving] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  // Meeting kind — "technical" enables the live architecture-diagram tab + summary
  // diagram; "training" produces a much longer, explanatory learning summary.
  const [meetingKind, setMeetingKind] = useState<"standard" | "technical" | "training">("standard");
  const technical = meetingKind === "technical";
  // Optional client industry/vertical (external meetings) — biases hints + use cases.
  const [industry, setIndustry] = useState("");
  const [isInternalMeeting, setIsInternalMeeting] = useState(false);
  // Per-meeting role (defaults to the user's last choice / profile role).
  const setupUser = useAuthStore((st) => st.user);
  const availableRoles = useAvailableRoles();
  const [role, setRole] = useState<UserRole | "">("");
  const effectiveRole: UserRole = (role ||
    setupUser?.settings?.lastChosenRole ||
    setupUser?.role ||
    "Customer Engineer") as UserRole;
  // Organizational labels/tags for this meeting (editable now; auto-suggested after
  // the meeting when there's transcript content). Persisted immediately on change.
  const [labels, setLabels] = useState<string[]>([]);
  function saveLabels(next: string[]) {
    setLabels(next);
    void preMeetingApi.patchMeeting(meetingId, { labels: next }).catch(() => {});
  }
  // Prep-question set for this meeting (defaults to technical/role preset when one exists).
  const [qKey, setQKey] = useState<string>("");
  const effectiveQKey =
    qKey ||
    (technical && CUSTOM_QUESTION_PRESETS.technical
      ? "technical"
      : CUSTOM_QUESTION_PRESETS[effectiveRole]
        ? effectiveRole
        : "");

  useEffect(() => {
    let cancelled = false;
    Promise.all([preMeetingApi.fetchMeeting(meetingId), preMeetingApi.fetchAllMeetings()])
      .then(([meeting, allMeetings]) => {
        if (cancelled) return;
        if (meeting) {
          s.hydrateFromMeeting(meeting);
          setMeetingKind(
            meeting.meetingType === "technical"
              ? "technical"
              : meeting.meetingType === "training"
                ? "training"
                : "standard",
          );
          setIsInternalMeeting(meeting.account?.isInternal === true);
          setIndustry(meeting.account?.industry ?? "");
          setLabels(meeting.labels ?? []);
        }
        s.setPriorMeetings(allMeetings.filter((m) => m.id !== meetingId));
        if (!meeting?.language || meeting.language === "auto") {
          // Prefer the user's remembered SPOKEN-language choice (last-choice-sticks);
          // otherwise fall back to their UI language.
          const settings = useAuthStore.getState().user?.settings;
          const spokenPref = settings?.spokenLanguage;
          if (spokenPref && spokenPref !== "auto") {
            s.patchFields({ language: spokenPref });
          } else if (settings?.language && settings.language !== "en") {
            // Normalize region-suffixed settings codes (pt-BR, zh-CN) to the
            // canonical LangCode the meeting model uses (pt, zh).
            s.patchFields({ language: userLangToLangCode(settings.language) });
          }
        }
        // Reload a previously-attached company profile so the rep sees it again.
        const slug = meeting?.account?.companyProfileSlug;
        if (slug) {
          companiesApi
            .get(slug)
            .then((p) => {
              if (!cancelled) s.setCompanyProfile(p);
            })
            .catch(() => {});
        }
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [meetingId]);

  // Company-name autocomplete source (cached profiles + prior meeting companies).
  useEffect(() => {
    let cancelled = false;
    companiesApi
      .list()
      .then((items) => {
        if (!cancelled) s.setCompanySuggestions(items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleResearch(refresh = false, website?: string) {
    const name = s.client.trim();
    if (!name) return;
    s.setResearchingCompany(true);
    try {
      const profile = await companiesApi.research({
        name,
        refresh,
        ...(website ? { website } : {}),
      });
      // Discard a stale response: if the user changed the company name while this
      // request was in flight, don't apply the old company's profile (that left
      // stale intel on screen and blocked a refetch for the new name).
      if (usePreMeetingStore.getState().client.trim().toLowerCase() !== name.toLowerCase()) return;
      s.setCompanyProfile(profile);
    } catch (err) {
      const current = usePreMeetingStore.getState().client.trim().toLowerCase();
      if (current === name.toLowerCase()) {
        toast.push({ tone: "error", message: (err as Error).message });
      }
    } finally {
      s.setResearchingCompany(false);
    }
  }

  useEffect(() => () => s.reset(), []);

  // Seed the assistance (hints/answers) language from the user's saved default so
  // their last choice sticks across meetings (unless they change it here or live).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pref = setupUser?.settings?.assistanceLanguage;
    if (pref && pref !== "auto") s.patchFields({ assistanceLanguage: pref });
  }, [setupUser?.settings?.assistanceLanguage]);

  // Research runs only on an explicit action — pressing Enter in the company field,
  // clicking "Company intel", or picking a suggestion — never while typing.

  const canStart = s.client.trim() !== "" && s.title.trim() !== "";

  async function handleStart() {
    setSaving(true);
    try {
      await preMeetingApi.patchMeeting(meetingId, {
        account: {
          name: s.client,
          ...(s.companyProfile ? { companyProfileSlug: s.companyProfile.slug } : {}),
          ...(!isInternalMeeting && industry.trim() ? { industry: industry.trim() } : {}),
        },
        title: s.title,
        stage: s.stage,
        goal: s.goal,
        role: effectiveRole,
        ...(effectiveQKey ? { customQuestionKey: effectiveQKey } : {}),
        language: s.language,
        assistanceLanguage: s.assistanceLanguage,
        ...(meetingKind !== "standard" ? { meetingType: meetingKind } : {}),
      });
      void authApi
        .updateSettings({
          lastChosenRole: effectiveRole,
          assistanceLanguage: s.assistanceLanguage,
          spokenLanguage: s.language,
        })
        .catch(() => {});
      nav({ to: "/meetings/$id/live", params: { id: meetingId } });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function handleSchedule() {
    if (!scheduleDate || !scheduleTime) {
      toast.push({ tone: "error", message: "Please select a date and time." });
      return;
    }
    setScheduling(true);
    try {
      await preMeetingApi.patchMeeting(meetingId, {
        account: {
          name: s.client,
          ...(s.companyProfile ? { companyProfileSlug: s.companyProfile.slug } : {}),
          ...(!isInternalMeeting && industry.trim() ? { industry: industry.trim() } : {}),
        },
        title: s.title,
        stage: s.stage,
        goal: s.goal,
        role: effectiveRole,
        ...(effectiveQKey ? { customQuestionKey: effectiveQKey } : {}),
        language: s.language,
        assistanceLanguage: s.assistanceLanguage,
        ...(meetingKind !== "standard" ? { meetingType: meetingKind } : {}),
      });
      void authApi
        .updateSettings({
          lastChosenRole: effectiveRole,
          assistanceLanguage: s.assistanceLanguage,
          spokenLanguage: s.language,
        })
        .catch(() => {});
      const scheduledAt = new Date(`${scheduleDate}T${scheduleTime}`).toISOString();
      await preMeetingApi.scheduleMeeting(meetingId, scheduledAt);
      toast.push({ tone: "success", message: "Meeting scheduled." });
      nav({ to: "/dashboard" });
    } catch (err) {
      toast.push({ tone: "error", message: (err as Error).message });
    } finally {
      setScheduling(false);
    }
  }

  if (loading) {
    return (
      <div className="setup">
        <div style={{ padding: 48, textAlign: "center", color: "var(--text-3)" }}>
          Loading meeting setup...
        </div>
      </div>
    );
  }

  return (
    <div className="setup">
      <div className="setup-top">
        <button type="button" className="ghost-btn" onClick={() => nav({ to: "/dashboard" })}>
          ← Cancel
        </button>
        <span className="setup-top-title">Set up your meeting</span>
      </div>

      <div className="setup-body setup-body-single">
        {/* Section 1: Client & Meeting */}
        <section className="setup-section">
          <h2 className="setup-section-head">Client & Meeting</h2>

          <Field
            label="Client / company"
            hint="Used to look up prior calls and research the company."
          >
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <CompanyPicker
                  value={s.client}
                  onChange={(v) => s.patchFields({ client: v })}
                  onSelect={(pick) => {
                    s.patchFields({ client: pick.name });
                    if (pick.hasProfile && pick.slug) {
                      s.setResearchingCompany(true);
                      companiesApi
                        .get(pick.slug)
                        .then((p) => s.setCompanyProfile(p))
                        .catch(() => {})
                        .finally(() => s.setResearchingCompany(false));
                    } else {
                      void handleResearch(false, pick.domain);
                    }
                  }}
                  priorSuggestions={s.companySuggestions}
                  placeholder="e.g. Aviv Capital"
                  autoFocus
                />
              </div>
              <Button
                variant="ghost"
                onClick={() => handleResearch(false)}
                loading={s.researchingCompany}
                disabled={!s.client.trim() || s.researchingCompany}
              >
                Company intel
              </Button>
            </div>
          </Field>

          <AutoFillBanner />

          {(s.companyProfile || s.researchingCompany) && (
            <CompanyReportPanel
              companyName={s.client.trim()}
              researchMarkdown={s.companyProfile?.markdown || s.companyProfile?.overview || ""}
              researchLoading={s.researchingCompany}
            />
          )}

          <Field label="Meeting title">
            <input
              type="text"
              className="setup-input"
              value={s.title}
              onChange={(e) => s.patchFields({ title: e.target.value })}
              placeholder="e.g. Vertex AI Migration · Technical deep-dive"
            />
          </Field>

          <Field label="Stage">
            <SegmentedControl<MeetingStage>
              options={[
                { value: "Intro", label: "Intro" },
                { value: "Discovery", label: "Discovery" },
                { value: "Qualification", label: "Qualification" },
                { value: "Negotiation", label: "Negotiation" },
              ]}
              value={s.stage}
              onChange={(v) => s.patchFields({ stage: v })}
            />
          </Field>

          <Field
            label="Meeting type"
            hint="Technical meetings get a live architecture-diagram tab and a diagram in the summary. Training sessions get a much longer, explanatory summary you can learn from."
          >
            <SegmentedControl<"standard" | "technical" | "training">
              options={[
                { value: "standard", label: "Standard" },
                { value: "technical", label: "Technical" },
                { value: "training", label: "Training / learning" },
              ]}
              value={meetingKind}
              onChange={setMeetingKind}
            />
          </Field>

          {!isInternalMeeting && (
            <Field
              label="Client industry (optional)"
              hint="Helps SellSynq tailor live hints and customer stories to this client's industry."
            >
              <input
                type="text"
                className="setup-input"
                list="industry-options"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                placeholder="e.g. Financial services, Healthcare, Retail, Manufacturing, Gaming, Public sector…"
              />
              <datalist id="industry-options">
                {[
                  "Financial services",
                  "Healthcare & life sciences",
                  "Retail & e-commerce",
                  "Manufacturing",
                  "Media & entertainment",
                  "Gaming",
                  "Telecommunications",
                  "Public sector & government",
                  "Education",
                  "Technology / software",
                  "Automotive",
                  "Energy & utilities",
                  "Travel & hospitality",
                  "Transportation & logistics",
                ].map((i) => (
                  <option key={i} value={i} />
                ))}
              </datalist>
            </Field>
          )}
        </section>

        {/* Section 2: Goal & Preferences */}
        <section className="setup-section">
          <h2 className="setup-section-head">Goal & Preferences</h2>

          <Field
            label="Your role for this meeting"
            hint="Tailors hints, follow-ups and the summary to your specialty. Defaults to your last choice."
          >
            <select
              className="setup-input"
              value={effectiveRole}
              onChange={(e) => setRole(e.target.value as UserRole)}
            >
              {availableRoles.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Meeting goal / topics"
            hint="Specificity matters — vague goals get generic hints. SellSynq reminds you of topics you haven't covered."
          >
            <textarea
              className="setup-textarea"
              rows={3}
              value={s.goal}
              onChange={(e) => s.patchFields({ goal: e.target.value })}
              placeholder="e.g. Probe latency requirements and current Bedrock spend; surface Model Garden + regional endpoints."
            />
          </Field>

          <Field
            label="Labels"
            hint="Tag this meeting (e.g. Migration, Security, Renewal) to group and search it later. SellSynq can also suggest labels after the meeting."
          >
            <MeetingLabels
              labels={labels}
              onChange={saveLabels}
              suggest={() => summaryApi.suggestLabels(meetingId)}
              compact
            />
          </Field>

          <Field
            label="Prep questions"
            hint="A ready-made question set for your role / meeting type, answered live from the call. Edit sets in Settings."
          >
            <select
              className="setup-input"
              value={effectiveQKey}
              onChange={(e) => setQKey(e.target.value)}
            >
              <option value="">My default questions</option>
              {Object.keys(CUSTOM_QUESTION_PRESETS).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </Field>

          <div className="setup-templates">
            <span className="setup-templates-label">Or pick a template:</span>
            {TEMPLATES.map((t) => (
              <button
                key={t.label}
                type="button"
                className="setup-template-chip"
                onClick={() => s.patchFields({ goal: t.goal })}
              >
                {t.label}
              </button>
            ))}
          </div>

          <Field label="Meeting language" hint="The language spoken in the meeting.">
            <select
              className="setup-select"
              value={s.language}
              onChange={(e) => s.patchFields({ language: e.target.value as "auto" | LangCode })}
            >
              <option value="auto">Auto-detect</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} ({l.labelEn})
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="Hints & answers language"
            hint="Language of the hints/answers SellSynq shows you on the live screen — can differ from the spoken language. You can change it live."
          >
            <select
              className="setup-select"
              value={s.assistanceLanguage}
              onChange={(e) =>
                s.patchFields({ assistanceLanguage: e.target.value as "auto" | LangCode })
              }
            >
              <option value="auto">Same as meeting</option>
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.label} ({l.labelEn})
                </option>
              ))}
            </select>
          </Field>

          <Field label="Hint tone">
            <SegmentedControl
              options={[
                { value: "direct", label: "Direct" },
                { value: "consultative", label: "Consultative" },
                { value: "brief", label: "Brief" },
              ]}
              value={s.hintTone}
              onChange={(v) =>
                s.patchFields({ hintTone: v as "direct" | "consultative" | "brief" })
              }
            />
          </Field>
        </section>

        {/* Actions */}
        <div className="setup-actions">
          <Button
            variant="primary"
            className="lg"
            onClick={handleStart}
            loading={saving}
            disabled={!canStart || saving}
          >
            Start meeting
          </Button>

          {!scheduleMode ? (
            <Button variant="ghost" onClick={() => setScheduleMode(true)}>
              Schedule for later
            </Button>
          ) : (
            <Card className="schedule-card">
              <div className="schedule-head">Schedule this meeting</div>
              <div className="schedule-row">
                <input
                  type="date"
                  className="setup-input"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  min={new Date().toISOString().split("T")[0]}
                />
                <input
                  type="time"
                  className="setup-input"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>
              <div className="schedule-actions">
                <Button
                  variant="primary"
                  onClick={handleSchedule}
                  loading={scheduling}
                  disabled={scheduling}
                >
                  Confirm schedule
                </Button>
                <Button variant="ghost" onClick={() => setScheduleMode(false)}>
                  Cancel
                </Button>
              </div>
            </Card>
          )}
        </div>

        {!canStart && (
          <p className="setup-validation-hint">
            Enter a client name and meeting title to continue.
          </p>
        )}
      </div>
    </div>
  );
}
